import { Outlet, useLocation, useParams } from 'react-router'
import DesktopSidebar from './sidebar/DesktopSidebar'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import StripePortalDialog from '@/account/components/StripePortalDialog'
import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import ReferralDialog from '@/referral/views/referral-dialog'
import { useSettings } from '@/settings/hooks/useSettings'
import { MattersActions } from '@/matter/store/slice'
import ManageMembersDialogContainer from '@/matter/components/manage-members/ManageMembersDialogContainer'
import CreateOrEditMatterDialog from '@/matter/components/matter-dialogs/CreateOrEditMatterDialog'
import DeleteMatterDialog from '@/matter/components/matter-dialogs/DeleteMatterDialog'
import AcceptMatterInviteDialog from '@/matter/components/matter-dialogs/AcceptMatterInviteDialog'
import { kRouteSegments } from '@/routing/paths'
import MatterBreadcrumbsChat from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsChat'
import { selectMatterById } from '@/matter/store/selectors'
import { canViewChats } from '@/matter/utils/matter-permissions'
import { useRestoreSessionMatter } from '@/matter/hooks/useRestoreSessionMatter'
import { usePersonalOutlookWorkspace } from '@/matter/hooks/usePersonalOutlookWorkspace'
import SubscriptionNoticeBanner from '@/account/components/subscription-notice-banner/SubscriptionNoticeBanner'
import DashboardMobileTopBar from './DashboardMobileTopBar'
import MobileSidebar from './sidebar/MobileSidebar'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import DraftExportProvider from '@/drafting/workspace/context/DraftExportProvider'
import DraftHeader from '@/drafting/workspace/components/DraftHeader'
import { selectChatPanelOpen } from '@/drafting/workspace/store/selectors'
import { useIsSmallChatBreakpoint } from '@/hooks/useIsSmallChatBreakpoint'
import { SettingsModalRoot } from '@/settings-modal'
export default function DashboardLayout() {
  // Store - UI State
  const dispatch = useAppDispatch()
  const uiState = useAppSelector((state: RootState) => state.uiState)
  const sideNavCollapsed = uiState.sideNavCollapsed
  const { getSettings } = useSettings()

  // Auth Context
  const authContext = useContext(AuthContext)
  const { user } = authContext
  const userIsAnon = user?.isAnonymous

  const { chatFeature, matterId: urlMatterId } = useParams()
  const validChatFeature: boolean = chatFeature === kRouteSegments.assistant || chatFeature === kRouteSegments.agentV2

  const { draftArtifactVersionId } = useDraftWorkspace()
  const isDraftWorkspace = chatFeature === kRouteSegments.agentV2 && Boolean(draftArtifactVersionId)
  const isSmallScreen = useIsSmallChatBreakpoint()
  const chatPanelOpen = useAppSelector((state: RootState) => (draftArtifactVersionId ? selectChatPanelOpen(state, { draftArtifactVersionId }) : false))
  const chatPanelDocked = chatPanelOpen && !isSmallScreen

  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlMatterId ?? null }))

  // Assume chat access until matters load so we don't flash the chrome away from users during
  // initial load. Under collaboration, collaborators can view chat history and start their own
  // chats, while continuing someone else's chat is blocked inside AgentV2ChatWindow.
  const canAccessChats = !mattersInitialLoadComplete || canViewChats(urlMatter)

  const shouldDisplayBreadcrumbsForFeature: boolean = !userIsAnon && validChatFeature && canAccessChats

  // Load settings and matters when dashboard mounts for authenticated users
  useEffect(() => {
    if (!userIsAnon) {
      getSettings()
      dispatch(MattersActions.refreshMatterList({ forceRefresh: false }))
    }
    // getSettings is omitted so this runs once per mount, not once per settings hook render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userIsAnon])

  // Restore the active matter from session storage
  useRestoreSessionMatter()
  usePersonalOutlookWorkspace()

  const location = useLocation()
  const isMattersPage = location.pathname === '/dashboard/matters'
  useEffect(() => {
    // Preventing the active matter to stay set when navigating to the matters list page
    // The single source of truth for the active matter is the URL
    // So when the user navigates to the matters list page, the active matter needs to be cleared and set to null
    if (isMattersPage) {
      dispatch(MattersActions.setActiveMatterId({ matterId: null }))
    }
  }, [isMattersPage, dispatch])

  const renderBreadcrumbHeader = () => {
    if (!isDraftWorkspace) return <MatterBreadcrumbsChat /> // normal chat - show chat header
    if (!chatPanelDocked) return <DraftHeader isSmallScreen={isSmallScreen} /> // full-width document - show draft header
    return null // chat docked beside the document - each panel renders its own header
  }

  return (
    <main className={'bg-surface-app flex flex-col h-screen overflow-hidden'}>
      {/* Global Dialogs */}
      <StripePortalDialog />
      <ReferralDialog />
      <CreateOrEditMatterDialog />
      <DeleteMatterDialog />
      <AcceptMatterInviteDialog />
      <ManageMembersDialogContainer />
      <SettingsModalRoot />

      {/* Subscription Notice Banner */}
      <SubscriptionNoticeBanner />

      {/* Mobile Only Components */}
      <div className="lg:hidden">
        {/* Mobile Top Bar */}
        <DashboardMobileTopBar />

        {/* Mobile Sidebar (slide-out) */}
        <MobileSidebar />
      </div>

      <div className={'flex-1 flex overflow-hidden'}>
        {/* Desktop Sidebar */}
        <div className={`hidden lg:flex h-full items-center`}>
          <DesktopSidebar collapsed={sideNavCollapsed} />
        </div>

        {/* On large screens the content column accounts for the side nav's width.
            The contracted width mirrors SIDENAV_CONTRACTED_WIDTH, which Tailwind cannot interpolate. */}
        <div className={`flex-1 flex flex-col w-full overflow-hidden ${sideNavCollapsed ? 'lg:w-[calc(100%-48px)]' : 'lg:w-[calc(100%-16rem)]'}`}>
          {/* The draft header and the draft document render in separate trees, so the export surface they share is owned above both. */}
          <DraftExportProvider>
            {shouldDisplayBreadcrumbsForFeature && renderBreadcrumbHeader()}

            <div className={`flex min-h-0 flex-1 overflow-y-auto`}>
              {/* Main Content */}
              <Outlet />
            </div>
          </DraftExportProvider>
        </div>
      </div>
    </main>
  )
}
